import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, ArrowRight, Camera, Upload, X, Save, PenTool, Droplets, Thermometer, Wrench, Plus, Trash2, Clock, Wifi, WifiOff, Cloud, CloudOff, CheckCircle, AlertCircle, Loader2, FileText, Star, Eye, Download, Edit3, Crop } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import PdfSignPlacer from '../components/PdfSignPlacer';
import PhotoEditor from '../components/PhotoEditor';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO = '/kardif-logo.png';
const LOCAL_DRAFT_KEY = 'kardif_intervention_draft';
const OFFLINE_QUEUE_KEY = 'kardif_offline_queue';
const DRAFT_SYNC_INTERVAL = 30000;
const AUTO_SAVE_DELAY = 5000; // 5 secondes au lieu de 2 - moins intrusif
const MAX_IMAGE_SIZE = 1200; // Taille max en pixels pour compression (augmenté pour meilleure qualité)
const IMAGE_QUALITY = 0.7; // Qualité JPEG 70%

// ==================== UTILITAIRE COMPRESSION IMAGE ====================
const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    // Si le fichier est petit (< 800Ko), pas besoin de compresser
    if (file.size < 800000) {
      console.log(`Image petite (${(file.size/1024).toFixed(0)}Ko), pas de compression`);
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => {
      console.warn('Erreur lecture fichier, envoi sans compression');
      resolve(file); // En cas d'erreur, envoyer le fichier original
    };
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => {
        console.warn('Erreur chargement image, envoi sans compression');
        resolve(file); // En cas d'erreur, envoyer le fichier original
      };
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          
          // Redimensionner si trop grand
          if (width > MAX_IMAGE_SIZE || height > MAX_IMAGE_SIZE) {
            if (width > height) {
              height = (height / width) * MAX_IMAGE_SIZE;
              width = MAX_IMAGE_SIZE;
            } else {
              width = (width / height) * MAX_IMAGE_SIZE;
              height = MAX_IMAGE_SIZE;
            }
          }
          
          canvas.width = Math.round(width);
          canvas.height = Math.round(height);
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
                console.log(`Image compressée: ${(file.size/1024).toFixed(0)}Ko -> ${(blob.size/1024).toFixed(0)}Ko`);
                resolve(compressedFile);
              } else {
                console.warn('Blob null, envoi sans compression');
                resolve(file);
              }
            },
            'image/jpeg',
            IMAGE_QUALITY
          );
        } catch (err) {
          console.warn('Erreur compression, envoi sans compression:', err);
          resolve(file);
        }
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

const TYPES_BAT = ['Maison', 'Immeuble', 'Local pro', 'Autre'];

// ==================== COMPOSANT PHOTO UPLOAD EXTERNE (OPTIMISÉ POUR iOS) ====================
// IMPORTANT: Ce composant est EXTERNE pour éviter les re-renders qui causent des bugs sur iOS Safari
const StablePhotoUpload = React.memo(({ onUpload, photos = [], onRemove, onEdit, title, multi = true, uploading = false, captions = [], onCaptionChange, showCaptions = false, captionTestIdPrefix = 'photo-legende' }) => {
  const [localProcessing, setLocalProcessing] = useState(false);
  const fileQueueRef = useRef([]);
  const processingRef = useRef(false);
  
  // Gestionnaire stable pour iOS - capture les fichiers IMMÉDIATEMENT avant tout re-render
  const handleFileChange = useCallback(async (e, isCamera = false) => {
    // CRITIQUE iOS: Ne JAMAIS appeler e.preventDefault() sur un input file
    const inputElement = e.target;
    const fileList = inputElement.files;
    
    if (!fileList || fileList.length === 0) {
      console.log('[iOS Debug] Aucun fichier détecté');
      return;
    }
    
    console.log(`[iOS Debug] ${fileList.length} fichier(s) capturé(s) via ${isCamera ? 'caméra' : 'galerie'}`);
    
    // CRITIQUE iOS: Copier les fichiers IMMÉDIATEMENT dans un tableau stable
    // car la FileList peut devenir invalide après un re-render
    const stableFiles = [];
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      if (file && file.size > 0) {
        // Créer un clone du fichier pour être sûr qu'il persiste
        stableFiles.push(new File([file], file.name || `photo_${Date.now()}_${i}.jpg`, { type: file.type || 'image/jpeg' }));
      }
    }
    
    // Réinitialiser l'input IMMÉDIATEMENT après copie
    inputElement.value = '';
    
    if (stableFiles.length === 0) {
      console.log('[iOS Debug] Aucun fichier valide après copie');
      return;
    }
    
    console.log(`[iOS Debug] ${stableFiles.length} fichier(s) stable(s) créé(s)`);
    
    // Ajouter à la queue et traiter
    fileQueueRef.current = [...fileQueueRef.current, ...stableFiles];
    
    // Éviter les traitements parallèles
    if (processingRef.current) {
      console.log('[iOS Debug] Traitement en cours, fichiers ajoutés à la queue');
      return;
    }
    
    processingRef.current = true;
    setLocalProcessing(true);
    
    try {
      // Traiter tous les fichiers de la queue
      while (fileQueueRef.current.length > 0) {
        const filesToProcess = [...fileQueueRef.current];
        fileQueueRef.current = [];
        
        console.log(`[iOS Debug] Envoi de ${filesToProcess.length} fichier(s)...`);
        await onUpload(filesToProcess);
      }
    } catch (err) {
      console.error('[iOS Debug] Erreur upload:', err);
    } finally {
      processingRef.current = false;
      setLocalProcessing(false);
    }
  }, [onUpload]);
  
  const isProcessing = localProcessing || uploading;
  
  return (
    <div className="space-y-3">
      {title && (
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">{title}</Label>
          {photos.length > 0 && (
            <span className="text-xs text-[#8B7355] font-medium bg-[#8B7355]/10 px-2 py-1 rounded-full">
              {photos.length} photo{photos.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}
      
      {/* Grille de photos existantes */}
      {photos.length > 0 && (
        <div className={`grid ${showCaptions ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-4'} gap-3`}>
          {photos.map((p, i) => (
            <div key={`photo-${i}-${typeof p === 'string' ? p.slice(-10) : i}`} className="flex flex-col">
              <div className="relative aspect-square">
                <img src={p} alt="" className="w-full h-full object-cover rounded-lg border-2 border-[#8B7355]/30" loading="lazy" />
                <button type="button" onClick={() => onRemove(i)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 shadow-lg hover:bg-red-600">
                  <X className="h-3 w-3" />
                </button>
                {onEdit && (
                  <button type="button" onClick={() => onEdit(i)} data-testid={`photo-edit-btn-${i}`}
                    className="absolute bottom-1 right-1 bg-[#8B7355] text-white rounded-md px-2 py-1 text-[10px] font-semibold shadow hover:bg-[#7A6548] flex items-center gap-1">
                    <Crop className="h-3 w-3" />Éditer
                  </button>
                )}
              </div>
              {showCaptions && (
                <DebouncedInput
                  value={captions?.[i] || ''}
                  onChange={(e) => onCaptionChange && onCaptionChange(i, e.target.value)}
                  placeholder={`Légende photo ${i + 1}`}
                  className="mt-1.5 text-xs h-8"
                  data-testid={`${captionTestIdPrefix}-${i}`}
                />
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Boutons d'ajout de photos - OPTIMISÉS iOS */}
      <div className="grid grid-cols-2 gap-3">
        {/* Bouton Galerie - iOS optimisé */}
        <div className="relative">
          <input 
            type="file" 
            accept="image/*" 
            onChange={(e) => handleFileChange(e, false)}
            multiple={multi}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          />
          <div className={`flex flex-col items-center justify-center gap-2 p-4 bg-[#8B7355]/10 border-2 border-[#8B7355] rounded-xl pointer-events-none ${isProcessing ? 'opacity-50' : ''}`}>
            {isProcessing ? (
              <Loader2 className="h-8 w-8 text-[#8B7355] animate-spin" />
            ) : (
              <Upload className="h-8 w-8 text-[#8B7355]" />
            )}
            <span className="text-sm font-semibold text-[#8B7355]">Galerie</span>
            <span className="text-xs text-gray-500">Plusieurs photos</span>
          </div>
        </div>
        
        {/* Bouton Caméra - iOS optimisé */}
        <div className="relative">
          <input 
            type="file" 
            accept="image/*" 
            capture="environment" 
            onChange={(e) => handleFileChange(e, true)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          />
          <div className={`flex flex-col items-center justify-center gap-2 p-4 bg-white border-2 border-dashed border-[#8B7355]/50 rounded-xl pointer-events-none ${isProcessing ? 'opacity-50' : ''}`}>
            {isProcessing ? (
              <Loader2 className="h-8 w-8 text-[#8B7355] animate-spin" />
            ) : (
              <Camera className="h-8 w-8 text-[#8B7355]" />
            )}
            <span className="text-sm font-semibold text-[#8B7355]">Appareil photo</span>
            <span className="text-xs text-gray-500">Prise directe</span>
          </div>
        </div>
      </div>
      
      {/* Astuce */}
      {photos.length === 0 && !isProcessing && (
        <p className="text-xs text-gray-400 text-center italic">
          💡 Touchez "Galerie" pour ajouter plusieurs photos
        </p>
      )}
      
      {isProcessing && (
        <p className="text-xs text-[#8B7355] text-center font-medium animate-pulse">
          ⏳ Traitement en cours...
        </p>
      )}
    </div>
  );
});

// Import React pour React.memo - déjà importé en haut du fichier
const TYPES_INT = ['Recherche de fuite', "Recherche d'infiltration", 'Les deux', 'Phénomène de pont thermique', 'Fuite visuelle', 'Piscine', 'Drone'];
const PIECES = ['Salon', 'Chambre', 'Cuisine', 'SDB', 'WC', 'Couloir', 'Cave', 'Garage', 'Terrasse', 'Autre'];
const STIGMATES = ['Auréoles', 'Moisissures', 'Gonflement matériaux', 'Fissures', 'Plinthes humides', 'Décollement peinture', 'Efflorescence', 'Cloques', 'Odeur'];
const PROCEDES_TYPES = {
  'Colorant': {
    description: "Injection de colorant fluorescent dans les canalisations pour tracer le parcours de l'eau et identifier les fuites. Le colorant permet de visualiser le cheminement de l'écoulement et de localiser précisément les points de fuite.",
    resultat: "Fuite identifiée / Non identifiée - Observations :"
  },
  'Fumigène': {
    description: "Test au fumigène réalisé sur le réseau d'évacuation. De la fumée est injectée dans les canalisations afin de détecter les défauts d'étanchéité et les points de pénétration dans le bâtiment.",
    resultat: "Infiltrations détectées / Réseau étanche - Observations :"
  },
  'Arrosage': {
    description: "Test d'arrosage effectué sur les zones suspectées (toiture, terrasse, façade, menuiseries). L'eau est projetée méthodiquement pour reproduire les conditions de pluie et identifier les points d'entrée d'eau.",
    resultat: "Points d'infiltration identifiés / Aucune infiltration détectée - Observations :"
  },
  'Arrosage localisé toiture': {
    description: "Test d'arrosage localisé effectué sur la toiture. L'eau est projetée méthodiquement sur les différentes zones de la toiture (faîtage, noues, rives, pénétrations) pour identifier les points d'entrée d'eau.",
    resultat: "Points d'infiltration toiture identifiés / Toiture étanche - Observations :"
  },
  'Arrosage localisé terrasse': {
    description: "Test d'arrosage localisé effectué sur la terrasse. L'eau est projetée sur les joints, relevés d'étanchéité, évacuations et seuils pour reproduire les conditions de pluie et identifier les infiltrations.",
    resultat: "Points d'infiltration terrasse identifiés / Terrasse étanche - Observations :"
  },
  'Arrosage localisé fenêtres': {
    description: "Test d'arrosage localisé effectué sur les fenêtres et menuiseries. L'eau est projetée sur les joints, dormants et appuis de fenêtres pour détecter les défauts d'étanchéité.",
    resultat: "Points d'infiltration fenêtres identifiés / Menuiseries étanches - Observations :"
  },
  'Test de pression manométrique': {
    description: "Test de pression manométrique réalisé sur le réseau d'alimentation en eau. Le réseau est mis sous pression et surveillé à l'aide d'un manomètre pour détecter toute chute de pression indiquant une fuite.",
    resultat: "Pression stable / Chute de pression détectée - Valeur initiale : ___ bar - Valeur finale : ___ bar - Observations :"
  },
  'Drone': {
    description: "Inspection aérienne par drone équipé d'une caméra haute définition et/ou thermique. Permet d'accéder aux zones difficiles (toitures, façades hautes) et d'obtenir une vue d'ensemble des désordres.",
    resultat: "Anomalies détectées / RAS - Observations :"
  },
  'Inspection PIN': {
    description: "Inspection par caméra PIN (Push-In) des canalisations. Cette technique permet d'inspecter les réseaux de petit diamètre et de localiser précisément les défauts, obstructions ou dégradations.",
    resultat: "État du réseau : Bon / Dégradé / Obstrué - Observations :"
  },
  'Inspection visuelle': {
    description: "Inspection visuelle approfondie des zones concernées. Examen minutieux des surfaces, joints, raccords et éléments de construction pour identifier les désordres visibles et les points de vulnérabilité.",
    resultat: "Désordres constatés / RAS - Observations :"
  },
  'Mise en charge': {
    description: "Mise en charge du réseau d'évacuation pour tester son étanchéité. Le réseau est obturé et rempli d'eau pour vérifier l'absence de fuite sous pression hydrostatique.",
    resultat: "Fuite détectée / Réseau étanche - Observations :"
  },
  'Coupure réseau': {
    description: "Coupure sélective des réseaux (eau froide, eau chaude, chauffage) pour isoler le réseau défaillant. Chaque réseau est coupé successivement et le compteur d'eau est surveillé.",
    resultat: "Réseau défaillant identifié / Tous les réseaux étanches - Observations :"
  },
  'Caméra endoscopique': {
    description: "Inspection par caméra endoscopique des canalisations et conduits. La caméra permet de visualiser l'état intérieur des réseaux et de localiser les obstructions, fissures ou dégradations.",
    resultat: "État du réseau : Bon / Dégradé / Obstrué - Observations :"
  },
  'Gaz traceur': {
    description: "Recherche de fuite par injection de gaz traceur (azote/hydrogène) dans les canalisations. Le détecteur de gaz permet de localiser précisément les points de fuite, même à travers les sols et murs.",
    resultat: "Fuite localisée / Aucune fuite détectée - Observations :"
  },
  'Écoute électro-acoustique': {
    description: "Détection de fuite par amplification sonore. L'appareil d'écoute électro-acoustique permet d'entendre le bruit caractéristique de l'eau qui fuit à travers les canalisations enterrées ou encastrées.",
    resultat: "Son de fuite détecté / Aucun son suspect - Observations :"
  },
  'Caméra thermique': {
    description: "Inspection par caméra thermique infrarouge pour détecter les variations de température. Cette technique permet de visualiser les infiltrations d'eau, les défauts d'isolation et les ponts thermiques.",
    resultat: "Anomalies thermiques détectées / RAS - Observations :"
  },
  'Humidimètre': {
    description: "Mesures d'humidité réalisées sur les parois, sols et plafonds à l'aide d'un humidimètre professionnel. Les valeurs permettent de quantifier et cartographier l'étendue des dégâts des eaux.",
    resultat: "Taux d'humidité relevés - Zones humides identifiées :"
  },
  'Fluorescéine': {
    description: "Application de fluorescéine (traceur UV) pour détecter les fuites. Sous lumière ultraviolette, le produit révèle le cheminement de l'eau et les points d'infiltration invisibles à l'œil nu.",
    resultat: "Traces fluorescentes détectées / Aucune trace - Observations :"
  },
  'Hygromètre': {
    description: "Mesure du taux d'humidité de l'air ambiant à l'aide d'un hygromètre. Le relevé permet d'évaluer les conditions hygrométriques des pièces et de détecter une humidité anormale favorisant les désordres.",
    resultat: "Taux d'humidité de l'air relevé : ___ % HR - Observations :"
  },
  'Anémomètre': {
    description: "Mesure de la vitesse et du débit d'air à l'aide d'un anémomètre. Cette mesure permet de contrôler la ventilation, les flux d'air et de détecter d'éventuelles entrées d'air parasites.",
    resultat: "Vitesse d'air relevée : ___ m/s - Observations :"
  },
  'Autre': {
    description: "",
    resultat: ""
  }
};

const PROCEDES_LISTE = Object.keys(PROCEDES_TYPES);

// ==================== COMPOSANT PHOTO SIMPLE POUR iOS ====================
const PhotoCapture = ({ onCapture, uploading, label = "Prendre une photo", subLabel = "Appareil photo", capture = true }) => {
  return (
    <div className={`relative ${capture ? 'p-8 border-[#8B7355]' : 'p-6 border-gray-300'} border-2 border-dashed rounded-xl text-center`}>
      <input 
        type="file" 
        accept="image/*" 
        capture={capture ? "environment" : undefined}
        onChange={(e) => {
          const files = e.target.files;
          if (files && files.length > 0) {
            onCapture(Array.from(files));
          }
          e.target.value = '';
        }}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      />
      <div className="pointer-events-none">
        {uploading ? (
          <Loader2 className="h-12 w-12 text-[#8B7355] mx-auto mb-3 animate-spin" />
        ) : capture ? (
          <Camera className="h-12 w-12 text-[#8B7355] mx-auto mb-3" />
        ) : (
          <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
        )}
        <p className={`font-semibold ${capture ? 'text-[#1A1A1A]' : 'text-gray-600'}`}>{label}</p>
        <p className="text-sm text-[#6B7280]">{subLabel}</p>
      </div>
    </div>
  );
};

// ==================== COMPOSANTS OPTIMISÉS POUR LA FRAPPE ====================
// Input optimisé - ne déclenche pas de re-render parent à chaque frappe
const DebouncedInput = ({ value, onChange, delay = 300, ...props }) => {
  const [localValue, setLocalValue] = useState(value);
  const timeoutRef = useRef(null);
  
  useEffect(() => {
    setLocalValue(value);
  }, [value]);
  
  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue); // Mise à jour locale instantanée
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      onChange({ target: { value: newValue } }); // Mise à jour parent après délai
    }, delay);
  };
  
  return <Input value={localValue} onChange={handleChange} {...props} />;
};

// Textarea optimisé - ne déclenche pas de re-render parent à chaque frappe
const DebouncedTextarea = ({ value, onChange, delay = 300, ...props }) => {
  const [localValue, setLocalValue] = useState(value);
  const timeoutRef = useRef(null);
  
  useEffect(() => {
    setLocalValue(value);
  }, [value]);
  
  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      onChange({ target: { value: newValue } });
    }, delay);
  };
  
  return <Textarea value={localValue} onChange={handleChange} {...props} />;
};

function SignaturePad({ onSave, title }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const isDrawingRef = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const hasDrawnRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    
    // Adapter la taille du canvas au conteneur
    if (container) {
      const width = Math.min(container.offsetWidth, 500);
      canvas.width = width;
      canvas.height = width / 2;
    }
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1A1A1A';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getPos = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else if (e.changedTouches && e.changedTouches.length > 0) {
      clientX = e.changedTouches[0].clientX;
      clientY = e.changedTouches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    return { 
      x: (clientX - rect.left) * scaleX, 
      y: (clientY - rect.top) * scaleY 
    };
  }, []);

  const startDrawing = useCallback((e) => { 
    e.preventDefault();
    e.stopPropagation();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d'); 
    const pos = getPos(e);
    
    ctx.beginPath(); 
    ctx.moveTo(pos.x, pos.y);
    lastPosRef.current = pos;
    isDrawingRef.current = true;
  }, [getPos]);
  
  const draw = useCallback((e) => { 
    if (!isDrawingRef.current) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d'); 
    const pos = getPos(e);
    
    // Dessiner une ligne du dernier point au point actuel
    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(pos.x, pos.y); 
    ctx.stroke();
    
    lastPosRef.current = pos;
    
    // Marquer comme dessiné
    if (!hasDrawnRef.current) {
      hasDrawnRef.current = true;
      setHasDrawn(true);
    }
  }, [getPos]);
  
  const stopDrawing = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    isDrawingRef.current = false;
  }, []);
  
  const clear = useCallback(() => { 
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d'); 
    ctx.fillStyle = 'white'; 
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1A1A1A';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    hasDrawnRef.current = false;
    setHasDrawn(false);
  }, []);

  const handleSave = useCallback(() => {
    if (!hasDrawnRef.current) {
      toast.error('Veuillez signer avant de valider');
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl);
    toast.success('Signature enregistrée !');
  }, [onSave]);

  return (
    <div className="space-y-4" ref={containerRef}>
      <p className="text-sm text-[#6B7280] font-medium">{title}</p>
      <div className="relative">
        <canvas 
          ref={canvasRef} 
          width={400} 
          height={200} 
          className={`border-[3px] ${hasDrawn ? 'border-green-500 shadow-md' : 'border-[#8B7355]'} rounded-xl w-full touch-none cursor-crosshair bg-white`} 
          style={{ maxWidth: '100%', height: 'auto', aspectRatio: '2/1', touchAction: 'none' }} 
          onMouseDown={startDrawing} 
          onMouseMove={draw} 
          onMouseUp={stopDrawing} 
          onMouseLeave={stopDrawing} 
          onTouchStart={startDrawing} 
          onTouchMove={draw} 
          onTouchEnd={stopDrawing}
          onTouchCancel={stopDrawing}
        />
        {!hasDrawn && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-[#9CA3AF] text-lg font-medium">Signez ici ✍️</p>
          </div>
        )}
      </div>
      <div className="flex gap-3">
        <Button variant="outline" onClick={clear} type="button" className="flex-1 border-2 border-[#8B7355]/60">
          Effacer
        </Button>
        <Button 
          onClick={handleSave} 
          className={`flex-1 ${hasDrawn ? 'bg-green-600 hover:bg-green-700' : 'bg-[#8B7355] hover:bg-[#7A6548]'} text-white font-semibold`} 
          type="button"
        >
          {hasDrawn ? '✓ Valider ma signature' : 'Valider signature'}
        </Button>
      </div>
      {hasDrawn && (
        <div className="bg-green-50 border-2 border-green-300 rounded-lg p-3 text-center">
          <p className="text-green-700 font-semibold">✓ Signature dessinée - Cliquez sur "Valider ma signature"</p>
        </div>
      )}
    </div>
  );
}

function InterventionForm() {
  const nav = useNavigate();
  const { missionId } = useParams();
  const [searchParams] = useSearchParams();
  const draftIdFromUrl = searchParams.get('draft');
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mission, setMission] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastSaved, setLastSaved] = useState(null);
  const [serverDraftId, setServerDraftId] = useState(draftIdFromUrl || null);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved, error, offline
  const [showDraftDialog, setShowDraftDialog] = useState(false);
  const [existingDrafts, setExistingDrafts] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [uploadingPJ, setUploadingPJ] = useState(false);
  const [photoEditor, setPhotoEditor] = useState({ open: false, src: null, onSaveUrl: null });
  const [editorUploading, setEditorUploading] = useState(false);
  const [signAtt, setSignAtt] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [signPlacement, setSignPlacement] = useState({ page: null, x: 0.5, y: 0.8, width_frac: 0.42, texts: [] });
  const [sigPreview, setSigPreview] = useState(null);
  const [signingPJ, setSigningPJ] = useState(false);
  const sigCanvasRef = useRef(null);
  const sigDrawingRef = useRef(false);

  const fileHref = (url) => (!url || url.startsWith('http') || url.startsWith('data:')) ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
  const sigStart = (e) => {
    sigDrawingRef.current = true;
    const c = sigCanvasRef.current; const r = c.getBoundingClientRect(); const ctx = c.getContext('2d');
    ctx.beginPath(); ctx.moveTo((e.clientX - r.left) * (c.width / r.width), (e.clientY - r.top) * (c.height / r.height));
  };
  const sigMove = (e) => {
    if (!sigDrawingRef.current) return;
    const c = sigCanvasRef.current; const r = c.getBoundingClientRect(); const ctx = c.getContext('2d');
    ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.strokeStyle = '#111';
    ctx.lineTo((e.clientX - r.left) * (c.width / r.width), (e.clientY - r.top) * (c.height / r.height)); ctx.stroke();
  };
  const sigEnd = () => { if (!sigDrawingRef.current) return; sigDrawingRef.current = false; try { setSigPreview(sigCanvasRef.current.toDataURL('image/png')); } catch (e) {} };
  const sigClear = () => { const c = sigCanvasRef.current; if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height); setSigPreview(null); };

  const submitSignPJ = async () => {
    const texts = signPlacement.texts || [];
    const drew = !!sigPreview;
    if (!drew && texts.length === 0) { toast.error('Ajoutez une signature ou un texte'); return; }
    setSigningPJ(true);
    try {
      const res = await axios.post(API + '/documents/sign', {
        url: signAtt.url,
        name: signAtt.name,
        signature: drew ? sigCanvasRef.current.toDataURL('image/png') : null,
        signer_name: signerName,
        page: signPlacement.page,
        x: signPlacement.x,
        y: signPlacement.y,
        width_frac: signPlacement.width_frac,
        texts,
      });
      const att = { ...res.data, uploaded_by_name: 'Signature client' };
      setForm(f => ({ ...f, pieces_jointes: [...(f.pieces_jointes || []), att] }));
      toast.success(drew ? 'Document signé ajouté' : 'Document annoté ajouté');
      setSignAtt(null); setSignerName(''); setSigPreview(null);
    } catch (e) { toast.error('Échec de la signature'); }
    finally { setSigningPJ(false); }
  };
  const totalSteps = 12;

  const initialForm = {
    heure_debut: null,
    heure_fin: null,
    photo_facade: null,
    adresse_complete: '',
    type_batiment: '',
    type_intervention: [],
    sinistre_statut: 'locataire',
    sinistrant_different: false,
    nom_sinistre: '',
    nom_sinistrant: '',
    etage_sinistre: '',
    etage_sinistrant: '',
    nombre_etages: '',
    pieces_sinistrees: [],
    constats_arrivee: '',
    duree_sinistre: '',
    client_nom: '',
    client_telephone: '',
    numero_sinistre: '',
    numero_dossier: '',
    nom_assurance: '',
    nom_expert: '',
    cabinet_expertise: '',
    photos_desordres: [],
    photos_desordres_legendes: [],
    photos_avant_apres: [],
    mesures_humidite: [{ zone: '', support: '', valeur: '', photos: [] }],
    mesures_thermiques: [{ zone: '', observation: '', photos: [] }],
    procedes: [{ type: '', nom: '', description: '', resultat: '', photos: [], photos_legendes: [] }],
    resume_constats: '',
    origine_probable: '',
    preconisations: '',
    points_urgents: '',
    franchise: false,
    franchise_montant: '',
    client_signature: null,
    documents_complementaires: [],
    pieces_jointes: [],
    mission_id: missionId || null,
    current_step: 1,
    satisfaction_note: null,
    attestation_text: "Je soussigné(e), certifie que l'intervention décrite ci-dessus a été réalisée à mon domicile / dans mes locaux. J'atteste avoir été informé(e) des constats et préconisations établis par le technicien. Je reconnais que les éléments photographiques joints à ce rapport reflètent fidèlement l'état des lieux au moment de l'intervention."
  };
  
  const [form, setForm] = useState(initialForm);
  const saveTimerRef = useRef(null);
  const lastSavedFormRef = useRef(null);

  // Vérifier connexion internet
  useEffect(() => {
    const handleOnline = () => { 
      setIsOnline(true); 
      toast.success('Connexion rétablie'); 
      syncOfflineData(); 
    };
    const handleOffline = () => { 
      setIsOnline(false); 
      toast.warning('Mode hors ligne activé - Données sauvegardées localement'); 
      setSaveStatus('offline');
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Charger brouillon existant depuis URL ou vérifier s'il y en a un local/serveur
  useEffect(() => {
    const initDraft = async () => {
      setInitialLoading(true);
      try {
        // Si on a un draft ID dans l'URL, le charger depuis le serveur
        if (draftIdFromUrl) {
          const res = await axios.get(`${API}/drafts/${draftIdFromUrl}`);
          setForm({ ...initialForm, ...res.data });
          setStep(res.data.current_step || 1);
          setServerDraftId(draftIdFromUrl);
          const mid = res.data.mission_id || missionId;
          if (mid) { mergedMissionRef.current = mid; applyMissionMerge(mid); }
          toast.success('Brouillon chargé');
          setInitialLoading(false);
          return;
        }

        // Vérifier s'il y a des brouillons serveur existants
        const draftsRes = await axios.get(`${API}/drafts`);
        const serverDrafts = draftsRes.data || [];
        
        // Vérifier aussi le localStorage
        const localDraft = localStorage.getItem(LOCAL_DRAFT_KEY);
        let localData = null;
        if (localDraft) {
          try {
            localData = JSON.parse(localDraft);
          } catch (e) {}
        }

        if (serverDrafts.length > 0 || localData) {
          setExistingDrafts(serverDrafts);
          setShowDraftDialog(true);
        }
      } catch (e) {
        // Si erreur réseau, vérifier localStorage
        const localDraft = localStorage.getItem(LOCAL_DRAFT_KEY);
        if (localDraft) {
          try {
            const localData = JSON.parse(localDraft);
            setExistingDrafts([{ ...localData, id: 'local', source: 'local' }]);
            setShowDraftDialog(true);
          } catch (e) {}
        }
      }
      setInitialLoading(false);
    };

    initDraft();
  }, [draftIdFromUrl]);

  // Charger + fusionner la mission (URL missionId OU mission_id d'un brouillon repris)
  const mergedMissionRef = useRef(null);
  const applyMissionMerge = useCallback(async (mid) => {
    if (!mid) return;
    try {
      const r = await axios.get(`${API}/missions/${mid}`);
      setMission(r.data);
      setForm(f => {
        const existing = f.pieces_jointes || [];
        const fromMission = (r.data.pieces_jointes || []).filter(m => !existing.some(p => p.id === m.id));
        return {
          ...f,
          adresse_complete: f.adresse_complete || r.data.address || '',
          client_nom: f.client_nom || r.data.client_name || '',
          client_telephone: f.client_telephone || r.data.client_phone || '',
          numero_sinistre: f.numero_sinistre || r.data.numero_sinistre || '',
          numero_dossier: f.numero_dossier || r.data.numero_dossier || '',
          nom_assurance: f.nom_assurance || r.data.nom_assurance || '',
          donneur_ordre_id: f.donneur_ordre_id || r.data.donneur_ordre_id || null,
          pieces_jointes: [...existing, ...fromMission],
          mission_id: mid,
        };
      });
    } catch (e) { /* mission introuvable */ }
  }, []);

  useEffect(() => {
    const mid = missionId || form.mission_id;
    if (!mid || mergedMissionRef.current === mid) return;
    mergedMissionRef.current = mid;
    applyMissionMerge(mid);
  }, [missionId, form.mission_id, applyMissionMerge]);

  // Fonction de sauvegarde vers le serveur - OPTIMISÉE (non-bloquante)
  const saveToServer = useCallback(async (formData, currentStep) => {
    // Exclure les photos/images du localStorage (trop gros)
    const localData = { 
      ...formData, 
      current_step: currentStep, 
      savedAt: new Date().toISOString(),
      // Exclure les champs avec des images base64 ou URLs de photos
      photo_facade: null,
      photos_desordres: [],
      client_signature: null,
      // Garder juste le compte des photos pour info
      _photo_count: {
        facade: formData.photo_facade ? 1 : 0,
        desordres: formData.photos_desordres?.length || 0,
        humidite: formData.mesures_humidite?.reduce((acc, m) => acc + (m.photos?.length || 0), 0) || 0,
        thermiques: formData.mesures_thermiques?.reduce((acc, m) => acc + (m.photos?.length || 0), 0) || 0,
        procedes: formData.procedes?.reduce((acc, p) => acc + (p.photos?.length || 0), 0) || 0
      }
    };
    
    // Nettoyer les photos des mesures aussi pour localStorage
    if (localData.mesures_humidite) {
      localData.mesures_humidite = localData.mesures_humidite.map(m => ({ ...m, photos: [] }));
    }
    if (localData.mesures_thermiques) {
      localData.mesures_thermiques = localData.mesures_thermiques.map(m => ({ ...m, photos: [] }));
    }
    if (localData.procedes) {
      localData.procedes = localData.procedes.map(p => ({ ...p, photos: [] }));
    }
    
    try {
      localStorage.setItem(LOCAL_DRAFT_KEY, JSON.stringify(localData));
    } catch (e) {
      console.warn('localStorage plein, sauvegarde locale ignorée');
    }
    
    if (!isOnline) {
      setSaveStatus('offline');
      return;
    }

    setSaveStatus('saving');
    
    // Sauvegarde serveur en arrière-plan (ne bloque pas l'UI) - AVEC les photos
    try {
      const dataToSave = { ...formData, current_step: currentStep };
      
      if (serverDraftId) {
        await axios.patch(`${API}/drafts/${serverDraftId}`, dataToSave);
      } else {
        const res = await axios.post(`${API}/drafts`, dataToSave);
        setServerDraftId(res.data.id);
      }
      
      setSaveStatus('saved');
      setLastSaved(new Date());
      lastSavedFormRef.current = JSON.stringify(formData);
      try { localStorage.removeItem(LOCAL_DRAFT_KEY); } catch(e) {}
    } catch (e) {
      setSaveStatus('error');
      console.error('Erreur sauvegarde serveur:', e);
    }
  }, [isOnline, serverDraftId]);

  // Auto-save OPTIMISÉ - délai plus long, ne bloque pas la frappe
  useEffect(() => {
    if (!form.photo_facade && !form.adresse_complete && step === 1) {
      return;
    }

    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    const currentFormStr = JSON.stringify(form);
    if (currentFormStr === lastSavedFormRef.current) {
      return;
    }

    // Sauvegarder seulement 5 secondes après le dernier changement
    saveTimerRef.current = setTimeout(() => {
      // Utiliser requestIdleCallback pour ne pas bloquer l'UI
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => saveToServer(form, step), { timeout: 2000 });
      } else {
        saveToServer(form, step);
      }
    }, AUTO_SAVE_DELAY);

    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [form, step, saveToServer]);

  // Sauvegarder avant de quitter la page
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (form.photo_facade || form.adresse_complete) {
        // Sauvegarde légère sans photos
        try {
          const lightData = {
            adresse_complete: form.adresse_complete,
            type_batiment: form.type_batiment,
            client_nom: form.client_nom,
            current_step: step,
            savedAt: new Date().toISOString()
          };
          localStorage.setItem(LOCAL_DRAFT_KEY, JSON.stringify(lightData));
        } catch(e) {}
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [form, step]);

  // Charger un brouillon existant
  const loadDraft = async (draft) => {
    if (draft.source === 'local' || draft.id === 'local') {
      // Charger depuis localStorage
      const localDraft = localStorage.getItem(LOCAL_DRAFT_KEY);
      if (localDraft) {
        const data = JSON.parse(localDraft);
        setForm({ ...initialForm, ...data });
        setStep(data.current_step || 1);
      }
    } else {
      // Charger depuis le serveur
      try {
        const res = await axios.get(`${API}/drafts/${draft.id}`);
        setForm({ ...initialForm, ...res.data });
        setStep(res.data.current_step || 1);
        setServerDraftId(draft.id);
        // Re-fusionner les PDF de la mission liée (sinon perdus au rechargement du brouillon)
        const mid = res.data.mission_id || missionId;
        if (mid) { mergedMissionRef.current = mid; applyMissionMerge(mid); }
      } catch (e) {
        toast.error('Erreur chargement brouillon');
      }
    }
    setShowDraftDialog(false);
    toast.success('Brouillon restauré');
  };

  // Nouveau formulaire (ignorer les brouillons)
  const startNew = () => {
    setForm(initialForm);
    setStep(1);
    setServerDraftId(null);
    setShowDraftDialog(false);
    localStorage.removeItem(LOCAL_DRAFT_KEY);
    // Ré-appliquer les infos + PDF de la mission sur le formulaire neuf
    if (missionId) { mergedMissionRef.current = missionId; applyMissionMerge(missionId); }
  };

  // Synchroniser données hors ligne
  const syncOfflineData = async () => {
    const queue = localStorage.getItem(OFFLINE_QUEUE_KEY);
    if (queue) {
      try {
        const items = JSON.parse(queue);
        for (const item of items) {
          await axios.post(item.url, item.data);
        }
        localStorage.removeItem(OFFLINE_QUEUE_KEY);
        toast.success('Données synchronisées');
      } catch (e) {
        toast.error('Erreur synchronisation');
      }
    }

    // Synchroniser aussi le brouillon local s'il existe
    const localDraft = localStorage.getItem(LOCAL_DRAFT_KEY);
    if (localDraft) {
      try {
        const data = JSON.parse(localDraft);
        if (serverDraftId) {
          await axios.patch(`${API}/drafts/${serverDraftId}`, data);
        } else {
          const res = await axios.post(`${API}/drafts`, data);
          setServerDraftId(res.data.id);
        }
        localStorage.removeItem(LOCAL_DRAFT_KEY);
        toast.success('Brouillon synchronisé');
      } catch (e) {
        console.error('Erreur sync brouillon:', e);
      }
    }
  };

  // Éditeur de photo (rogner / tourner) — remplace la photo à sa position
  const uploadEditedBlob = async (blob) => {
    const fd = new FormData();
    fd.append('file', new File([blob], `edited_${Date.now()}.jpg`, { type: 'image/jpeg' }));
    const res = await axios.post(API + '/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
    let url = res.data?.url;
    if (url && url.startsWith('/')) url = process.env.REACT_APP_BACKEND_URL + url;
    return url;
  };

  const openPhotoEditor = (src, onSaveUrl) => setPhotoEditor({ open: true, src, onSaveUrl });

  const handlePhotoEditorSave = async (blob) => {
    setEditorUploading(true);
    try {
      const url = await uploadEditedBlob(blob);
      if (!url) { toast.error('Échec de l\'enregistrement'); return; }
      if (photoEditor.onSaveUrl) photoEditor.onSaveUrl(url);
      toast.success('Photo modifiée');
      setPhotoEditor({ open: false, src: null, onSaveUrl: null });
    } catch (e) {
      toast.error('Erreur lors de la modification');
    } finally {
      setEditorUploading(false);
    }
  };

  const editArrayPhoto = (arrayField, arrayIndex, photoIndex) => {
    const src = form[arrayField]?.[arrayIndex]?.photos?.[photoIndex];
    if (!src) return;
    openPhotoEditor(src, (url) => setForm(prev => {
      const arr = JSON.parse(JSON.stringify(prev[arrayField]));
      arr[arrayIndex].photos[photoIndex] = url;
      return { ...prev, [arrayField]: arr };
    }));
  };

  const editFieldPhoto = (field, photoIndex) => {
    const src = form[field]?.[photoIndex];
    if (!src) return;
    openPhotoEditor(src, (url) => setForm(prev => {
      const photos = [...(prev[field] || [])];
      photos[photoIndex] = url;
      return { ...prev, [field]: photos };
    }));
  };

  const editFacadePhoto = () => {
    if (!form.photo_facade) return;
    openPhotoEditor(form.photo_facade, (url) => setForm(prev => ({ ...prev, photo_facade: url })));
  };


  // Upload photo - CORRIGÉ pour iOS/mobile - accepte soit un event soit un array de fichiers
  const uploadPhoto = async (input, field, multi, arrayIndex = null, arrayField = null) => {
    // Déterminer si c'est un event ou un array de fichiers
    let files = [];
    
    if (Array.isArray(input)) {
      files = input.filter(f => f && f.size > 0);
    } else if (input?.target?.files) {
      files = Array.from(input.target.files).filter(f => f && f.size > 0);
    }
    
    if (!files.length) {
      console.log('Aucun fichier valide détecté');
      return;
    }
    
    console.log(`Upload de ${files.length} fichier(s)...`);
    setUploading(true);
    
    // Traiter chaque fichier
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        console.log(`Traitement fichier ${i + 1}/${files.length}: ${file.name}, taille: ${file.size}`);
        
        // Compresser l'image avec fallback
        let fileToUpload = file;
        try {
          const compressed = await compressImage(file);
          if (compressed && compressed.size > 0) {
            fileToUpload = compressed;
            console.log(`Compression réussie: ${file.size} -> ${compressed.size}`);
          }
        } catch (compressErr) {
          console.warn('Compression échouée, utilisation du fichier original:', compressErr);
        }
        
        // Afficher toast
        toast.info(`Envoi photo ${i + 1}/${files.length}...`, { duration: 2000 });
        
        // Créer FormData
        const fd = new FormData();
        fd.append('file', fileToUpload, file.name || `photo_${Date.now()}.jpg`);
        
        // Upload avec retry
        let uploadSuccess = false;
        let retries = 0;
        const maxRetries = 2;
        let url = null;
        
        while (!uploadSuccess && retries <= maxRetries) {
          try {
            const response = await axios.post(API + '/upload', fd, { 
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 60000
            });
            
            if (response.data?.url) {
              url = response.data.url;
              if (url.startsWith('/')) {
                url = process.env.REACT_APP_BACKEND_URL + url;
              }
              uploadSuccess = true;
              console.log(`Upload réussi: ${url}`);
            } else {
              throw new Error('URL manquante dans la réponse');
            }
          } catch (uploadErr) {
            retries++;
            console.warn(`Tentative ${retries}/${maxRetries} échouée:`, uploadErr.message);
            if (retries <= maxRetries) {
              await new Promise(r => setTimeout(r, 1000)); // Attendre 1s avant retry
            }
          }
        }
        
        if (!uploadSuccess || !url) {
          toast.error(`Échec envoi photo ${i + 1}`);
          continue;
        }
        
        // Si c'est la photo de façade, enregistrer l'heure de début
        if (field === 'photo_facade' && !form.heure_debut) {
          setForm(prev => ({ ...prev, heure_debut: new Date().toISOString() }));
        }
        
        // Mise à jour de l'état - IMMÉDIATE et SYNCHRONE
        if (arrayIndex !== null && arrayField) {
          setForm(prev => {
            const newArray = JSON.parse(JSON.stringify(prev[arrayField]));
            if (!newArray[arrayIndex]) {
              console.error(`Index ${arrayIndex} n'existe pas dans ${arrayField}`);
              return prev;
            }
            if (!Array.isArray(newArray[arrayIndex].photos)) {
              newArray[arrayIndex].photos = [];
            }
            // Vérifier que l'URL n'est pas déjà présente
            if (!newArray[arrayIndex].photos.includes(url)) {
              newArray[arrayIndex].photos.push(url);
            }
            console.log(`Photo ajoutée à ${arrayField}[${arrayIndex}].photos:`, newArray[arrayIndex].photos.length);
            return { ...prev, [arrayField]: newArray };
          });
        } else if (multi) {
          setForm(prev => {
            const currentPhotos = Array.isArray(prev[field]) ? prev[field] : [];
            if (!currentPhotos.includes(url)) {
              return { ...prev, [field]: [...currentPhotos, url] };
            }
            return prev;
          });
        } else {
          setForm(prev => ({ ...prev, [field]: url }));
        }
        
        toast.success(`Photo ${i + 1} ajoutée !`, { duration: 1500 });
        
      } catch (err) {
        console.error('Erreur traitement fichier:', err);
        toast.error(`Erreur photo ${i + 1}`);
      }
    }
    
    setUploading(false);
    console.log('Upload terminé');
  };

  const removePhoto = (field, idx, arrayIndex = null, arrayField = null) => {
    if (arrayIndex !== null && arrayField) {
      setForm(p => {
        const newArray = [...p[arrayField]];
        newArray[arrayIndex] = { ...newArray[arrayIndex] };
        newArray[arrayIndex].photos = newArray[arrayIndex].photos.filter((_, i) => i !== idx);
        if (Array.isArray(newArray[arrayIndex].photos_legendes)) {
          newArray[arrayIndex].photos_legendes = newArray[arrayIndex].photos_legendes.filter((_, i) => i !== idx);
        }
        return { ...p, [arrayField]: newArray };
      });
    } else if (idx !== null) {
      setForm(p => {
        const next = { ...p, [field]: p[field].filter((_, i) => i !== idx) };
        if (field === 'photos_desordres' && Array.isArray(p.photos_desordres_legendes)) {
          next.photos_desordres_legendes = p.photos_desordres_legendes.filter((_, i) => i !== idx);
        }
        return next;
      });
    } else {
      setForm(p => ({ ...p, [field]: null }));
    }
  };

  // ===== Photos Avant / Après (comparaison côte à côte) =====
  const addAvantApresPair = () => setForm(p => ({ ...p, photos_avant_apres: [...(p.photos_avant_apres || []), { avant: null, apres: null, legende: '' }] }));
  const removeAvantApresPair = (i) => setForm(p => ({ ...p, photos_avant_apres: p.photos_avant_apres.filter((_, idx) => idx !== i) }));

  const uploadAvantApres = async (input, pairIndex, which) => {
    let files = [];
    if (Array.isArray(input)) files = input.filter(f => f && f.size > 0);
    else if (input?.target?.files) files = Array.from(input.target.files).filter(f => f && f.size > 0);
    if (!files.length) return;
    const file = files[0];
    setUploading(true);
    try {
      let fileToUpload = file;
      try { const c = await compressImage(file); if (c && c.size > 0) fileToUpload = c; } catch (e) {}
      toast.info('Envoi photo...', { duration: 1500 });
      const fd = new FormData();
      fd.append('file', fileToUpload, file.name || `photo_${Date.now()}.jpg`);
      const response = await axios.post(API + '/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      let url = response.data?.url;
      if (url && url.startsWith('/')) url = process.env.REACT_APP_BACKEND_URL + url;
      if (url) {
        setForm(prev => {
          const arr = JSON.parse(JSON.stringify(prev.photos_avant_apres || []));
          if (arr[pairIndex]) arr[pairIndex][which] = url;
          return { ...prev, photos_avant_apres: arr };
        });
        toast.success('Photo ajoutée !', { duration: 1200 });
      } else {
        toast.error('Échec envoi photo');
      }
    } catch (e) {
      toast.error('Erreur envoi photo');
    } finally {
      setUploading(false);
    }
  };

  const removeAvantApresPhoto = (pairIndex, which) => setForm(prev => {
    const arr = [...(prev.photos_avant_apres || [])];
    if (arr[pairIndex]) arr[pairIndex] = { ...arr[pairIndex], [which]: null };
    return { ...prev, photos_avant_apres: arr };
  });

  // Ajout/suppression mesures et procédés
  const addMesureHumidite = () => setForm(p => ({ ...p, mesures_humidite: [...p.mesures_humidite, { zone: '', support: '', valeur: '', photos: [] }] }));
  const removeMesureHumidite = (i) => setForm(p => ({ ...p, mesures_humidite: p.mesures_humidite.filter((_, idx) => idx !== i) }));
  
  const addMesureThermique = () => setForm(p => ({ ...p, mesures_thermiques: [...p.mesures_thermiques, { zone: '', observation: '', photos: [] }] }));
  const removeMesureThermique = (i) => setForm(p => ({ ...p, mesures_thermiques: p.mesures_thermiques.filter((_, idx) => idx !== i) }));
  
  const addProcede = () => setForm(p => ({ ...p, procedes: [...p.procedes, { type: '', nom: '', description: '', resultat: '', photos: [], photos_legendes: [] }] }));
  const removeProcede = (i) => setForm(p => ({ ...p, procedes: p.procedes.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    if (!form.client_signature) { toast.error('La signature du client est obligatoire'); return; }
    setLoading(true);
    
    // Enregistrer l'heure de fin automatiquement
    const finalForm = { ...form, heure_fin: new Date().toISOString() };
    
    try {
      if (serverDraftId) {
        // Sauvegarder l'état courant (dont pièces jointes signées) avant de soumettre
        await axios.patch(`${API}/drafts/${serverDraftId}`, { ...finalForm, current_step: totalSteps });
        // Soumettre via le brouillon serveur
        await axios.post(`${API}/drafts/${serverDraftId}/submit`);
      } else {
        // Créer directement l'intervention
        await axios.post(API + '/interventions', finalForm);
      }
      
      // Nettoyer le brouillon local
      localStorage.removeItem(LOCAL_DRAFT_KEY);
      
      toast.success('Intervention créée et signée');
      nav('/dashboard');
    } catch (e) { 
      toast.error(e.response?.data?.detail || 'Erreur lors de la soumission'); 
    }
    setLoading(false);
  };

  const progress = (step / totalSteps) * 100;

  // Indicateur de statut de sauvegarde
  const SaveStatusIndicator = () => {
    const getStatusContent = () => {
      switch (saveStatus) {
        case 'saving':
          return { icon: <Loader2 className="h-3 w-3 animate-spin" />, text: 'Sauvegarde...', color: 'text-blue-500' };
        case 'saved':
          return { icon: <CheckCircle className="h-3 w-3" />, text: lastSaved ? `Sauvegardé ${lastSaved.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : 'Sauvegardé', color: 'text-green-500' };
        case 'error':
          return { icon: <AlertCircle className="h-3 w-3" />, text: 'Erreur sauvegarde', color: 'text-red-500' };
        case 'offline':
          return { icon: <CloudOff className="h-3 w-3" />, text: 'Sauvé localement', color: 'text-orange-500' };
        default:
          return null;
      }
    };
    
    const status = getStatusContent();
    if (!status) return null;
    
    return (
      <div className={`flex items-center gap-1 text-xs ${status.color}`}>
        {status.icon}
        <span className="hidden sm:inline">{status.text}</span>
      </div>
    );
  };

  // Dialogue pour choisir un brouillon
  const DraftDialog = () => {
    if (!showDraftDialog) return null;
    
    const localDraft = localStorage.getItem(LOCAL_DRAFT_KEY);
    let localData = null;
    if (localDraft) {
      try { localData = JSON.parse(localDraft); } catch (e) {}
    }

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl">
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#8B7355]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Cloud className="h-8 w-8 text-[#8B7355]" />
              </div>
              <h2 className="text-xl font-bold text-[#1A1A1A]">Brouillon trouvé</h2>
              <p className="text-[#6B7280] text-sm mt-1">Voulez-vous reprendre une intervention en cours ?</p>
            </div>

            <div className="space-y-3 mb-6 max-h-60 overflow-y-auto">
              {/* Brouillon local */}
              {localData && (
                <button
                  onClick={() => loadDraft({ id: 'local', source: 'local', ...localData })}
                  className="w-full p-4 border-2 border-orange-200 bg-orange-50 rounded-xl text-left hover:border-orange-400 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <CloudOff className="h-4 w-4 text-orange-500" />
                    <span className="font-semibold text-sm">Brouillon local</span>
                  </div>
                  <p className="text-xs text-gray-600 truncate">
                    {localData.adresse_complete || 'Adresse non renseignée'}
                  </p>
                  <p className="text-xs text-gray-500">
                    Étape {localData.current_step || 1}/{totalSteps} • {localData.savedAt ? new Date(localData.savedAt).toLocaleString('fr-FR') : ''}
                  </p>
                </button>
              )}

              {/* Brouillons serveur */}
              {existingDrafts.map((draft) => (
                <button
                  key={draft.id}
                  onClick={() => loadDraft(draft)}
                  className="w-full p-4 border-2 border-gray-200 bg-gray-50 rounded-xl text-left hover:border-[#8B7355] transition-colors"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Cloud className="h-4 w-4 text-[#8B7355]" />
                    <span className="font-semibold text-sm">Brouillon serveur</span>
                  </div>
                  <p className="text-xs text-gray-600 truncate">
                    {draft.adresse_complete || 'Adresse non renseignée'}
                  </p>
                  <p className="text-xs text-gray-500">
                    Étape {draft.current_step || 1}/{totalSteps} • {new Date(draft.updated_at).toLocaleString('fr-FR')}
                  </p>
                </button>
              ))}
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={startNew}
                className="flex-1"
              >
                Nouveau
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Loading initial
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A1A1A] via-[#2D2D2D] to-[#1A1A1A] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-[#8B7355] animate-spin mx-auto mb-4" />
          <p className="text-white">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A1A1A] via-[#2D2D2D] to-[#1A1A1A] pb-24">
      {/* Dialogue brouillon */}
      <DraftDialog />
      
      {/* Header mobile-friendly */}
      <header className="bg-black/50 backdrop-blur-lg sticky top-0 z-40 border-b border-[#8B7355]/30">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/kardif-eye.png" alt="KARDIF" className="h-9 w-9 object-contain" />
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-white font-semibold text-sm">Nouvelle intervention</h1>
                  {/* Indicateur connexion */}
                  {isOnline ? (
                    <Wifi className="h-3 w-3 text-green-400" />
                  ) : (
                    <WifiOff className="h-3 w-3 text-orange-400" />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-[#8B7355] text-xs">Étape {step}/{totalSteps}</p>
                  <SaveStatusIndicator />
                </div>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => nav('/dashboard')} className="text-white">
              <X className="h-5 w-5" />
            </Button>
          </div>
          <Progress value={progress} className="h-1 mt-3 bg-white/20" />
        </div>
      </header>

      {/* Mission info */}
      {mission && (
        <div className="max-w-2xl mx-auto px-4 pt-4">
          <div className="bg-[#8B7355]/20 border border-[#8B7355]/30 rounded-lg p-3">
            <p className="text-[#8B7355] text-sm font-medium">{mission.title}</p>
            <p className="text-white/70 text-xs">{mission.address}</p>
          </div>
        </div>
      )}

      <main className="max-w-2xl mx-auto px-4 py-6">
        <Card className="bg-white/95 backdrop-blur border-0 shadow-2xl">
          <CardContent className="p-5 sm:p-8">
            
            {/* ÉTAPE 1: Photo façade */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-[#8B7355]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Camera className="h-8 w-8 text-[#8B7355]" />
                  </div>
                  <h2 className="text-xl font-bold text-[#1A1A1A]">Photo de la façade</h2>
                  <p className="text-[#6B7280] text-sm mt-1">Cette photo marque le début de l'intervention</p>
                </div>
                {form.photo_facade ? (
                  <div className="relative max-w-md mx-auto">
                    <img src={form.photo_facade} alt="Façade" className="w-full rounded-xl shadow-lg" />
                    <button onClick={() => removePhoto('photo_facade', null)} className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 shadow-lg">
                      <X className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={editFacadePhoto} data-testid="facade-edit-btn" className="absolute bottom-2 right-2 bg-[#8B7355] text-white rounded-md px-3 py-1.5 text-xs font-semibold shadow hover:bg-[#7A6548] flex items-center gap-1">
                      <Crop className="h-3.5 w-3.5" />Éditer
                    </button>
                    {form.heure_debut && (
                      <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(form.heure_debut).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="max-w-md mx-auto space-y-4">
                    <PhotoCapture 
                      onCapture={(files) => uploadPhoto(files, 'photo_facade', false)}
                      uploading={uploading}
                      label="Prendre une photo"
                      subLabel="Appareil photo"
                      capture={true}
                    />
                    <PhotoCapture 
                      onCapture={(files) => uploadPhoto(files, 'photo_facade', false)}
                      uploading={uploading}
                      label="Depuis la galerie"
                      subLabel="Choisir une image"
                      capture={false}
                    />
                  </div>
                )}
              </div>
            )}

            {/* ÉTAPE 2: Lieu */}
            {step === 2 && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-center text-[#1A1A1A]">Lieu d'intervention</h2>
                <div>
                  <Label className="text-sm font-medium">Adresse complète *</Label>
                  <Textarea value={form.adresse_complete} onChange={(e) => setForm({ ...form, adresse_complete: e.target.value })} className="mt-2" rows={3} placeholder="Numéro, rue, code postal, ville" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#1A1A1A]">Type de bâtiment *</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {TYPES_BAT.map((t) => (
                      <button key={t} type="button" onClick={() => setForm({ ...form, type_batiment: t })}
                        className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${form.type_batiment === t ? 'border-[#8B7355] bg-[#8B7355]/10 shadow-md' : 'border-[#8B7355]/60 hover:border-[#8B7355] bg-white'}`}>
                        <div className={`w-6 h-6 rounded-full border-[3px] flex items-center justify-center ${form.type_batiment === t ? 'border-[#8B7355] bg-[#8B7355]' : 'border-[#8B7355] bg-white'}`}>
                          {form.type_batiment === t && <div className="w-3 h-3 bg-white rounded-full" />}
                        </div>
                        <span className={`font-medium ${form.type_batiment === t ? 'text-[#8B7355]' : 'text-[#1A1A1A]'}`}>{t}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ÉTAPE 3: Type intervention */}
            {step === 3 && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-center text-[#1A1A1A]">Type d'intervention</h2>
                <p className="text-center text-[#6B7280] text-sm">Sélectionnez un ou plusieurs types</p>
                <div className="space-y-3">
                  {TYPES_INT.map((t) => (
                    <button key={t} type="button" onClick={() => {
                      if (form.type_intervention.includes(t)) setForm({ ...form, type_intervention: form.type_intervention.filter(x => x !== t) });
                      else setForm({ ...form, type_intervention: [...form.type_intervention, t] });
                    }}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-4 ${form.type_intervention.includes(t) ? 'border-[#8B7355] bg-[#8B7355]/10 shadow-md' : 'border-[#8B7355]/60 hover:border-[#8B7355] bg-white'}`}>
                      <div className={`w-7 h-7 rounded-md border-[3px] flex items-center justify-center transition-all ${form.type_intervention.includes(t) ? 'border-[#8B7355] bg-[#8B7355]' : 'border-[#8B7355] bg-white'}`}>
                        {form.type_intervention.includes(t) && <span className="text-white text-lg font-bold">✓</span>}
                      </div>
                      <span className={`font-medium ${form.type_intervention.includes(t) ? 'text-[#8B7355]' : 'text-[#1A1A1A]'}`}>{t}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ÉTAPE 4: Sinistré */}
            {step === 4 && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-center text-[#1A1A1A]">Sinistré / Sinistrant</h2>
                <div className="flex gap-3">
                  {['locataire', 'proprietaire'].map((s) => (
                    <button key={s} type="button" onClick={() => setForm({ ...form, sinistre_statut: s })}
                      className={`flex-1 p-4 rounded-xl border-2 capitalize transition-all flex items-center justify-center gap-3 ${form.sinistre_statut === s ? 'border-[#8B7355] bg-[#8B7355]/10 shadow-md' : 'border-[#8B7355]/60 hover:border-[#8B7355] bg-white'}`}>
                      <div className={`w-6 h-6 rounded-full border-[3px] flex items-center justify-center ${form.sinistre_statut === s ? 'border-[#8B7355] bg-[#8B7355]' : 'border-[#8B7355] bg-white'}`}>
                        {form.sinistre_statut === s && <div className="w-3 h-3 bg-white rounded-full" />}
                      </div>
                      <span className={`font-medium ${form.sinistre_statut === s ? 'text-[#8B7355]' : 'text-[#1A1A1A]'}`}>
                        {s === 'locataire' ? 'Locataire' : 'Propriétaire'}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-4 p-4 border-2 border-[#8B7355]/60 rounded-xl bg-white hover:bg-[#8B7355]/5 cursor-pointer" onClick={() => setForm({ ...form, sinistrant_different: !form.sinistrant_different })}>
                  <div className={`w-7 h-7 rounded-md border-[3px] flex items-center justify-center transition-all ${form.sinistrant_different ? 'border-[#8B7355] bg-[#8B7355]' : 'border-[#8B7355] bg-white'}`}>
                    {form.sinistrant_different && <span className="text-white text-lg font-bold">✓</span>}
                  </div>
                  <Label className="cursor-pointer text-[#1A1A1A] font-medium">Sinistrant différent du sinistré</Label>
                </div>
                <div><Label className="text-[#1A1A1A] font-medium">Nom du sinistré</Label><Input value={form.nom_sinistre} onChange={(e) => setForm({ ...form, nom_sinistre: e.target.value })} className="mt-2 border-2" /></div>
                {form.sinistrant_different && <div><Label className="text-[#1A1A1A] font-medium">Nom du sinistrant</Label><Input value={form.nom_sinistrant} onChange={(e) => setForm({ ...form, nom_sinistrant: e.target.value })} className="mt-2 border-2" /></div>}
                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="text-sm text-[#1A1A1A]">Étage sinistré</Label><Input value={form.etage_sinistre} onChange={(e) => setForm({ ...form, etage_sinistre: e.target.value })} className="mt-1 border-2" /></div>
                  <div><Label className="text-sm text-[#1A1A1A]">Étage sinistrant</Label><Input value={form.etage_sinistrant} onChange={(e) => setForm({ ...form, etage_sinistrant: e.target.value })} className="mt-1 border-2" /></div>
                  <div><Label className="text-sm text-[#1A1A1A]">Nb étages</Label><Input value={form.nombre_etages} onChange={(e) => setForm({ ...form, nombre_etages: e.target.value })} className="mt-1 border-2" /></div>
                </div>
              </div>
            )}

            {/* ÉTAPE 5: Pièces */}
            {step === 5 && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-center text-[#1A1A1A]">Pièces sinistrées</h2>
                <p className="text-center text-[#6B7280] text-sm">Sélectionnez les pièces concernées</p>
                <div className="space-y-3">
                  {PIECES.map((piece) => {
                    const sel = form.pieces_sinistrees.find(p => p.piece === piece);
                    const idx = form.pieces_sinistrees.findIndex(p => p.piece === piece);
                    return (
                      <div key={piece} className={`border-2 rounded-xl overflow-hidden transition-all ${sel ? 'border-[#8B7355] shadow-md' : 'border-[#8B7355]/60'}`}>
                        <button type="button" className={`w-full flex items-center gap-4 p-4 ${sel ? 'bg-[#8B7355]/10' : 'bg-white hover:bg-gray-50'}`} onClick={() => {
                          if (sel) setForm({ ...form, pieces_sinistrees: form.pieces_sinistrees.filter(p => p.piece !== piece) });
                          else setForm({ ...form, pieces_sinistrees: [...form.pieces_sinistrees, { piece, stigmates: [] }] });
                        }}>
                          <div className={`w-7 h-7 rounded-md border-[3px] flex items-center justify-center transition-all ${sel ? 'border-[#8B7355] bg-[#8B7355]' : 'border-[#8B7355] bg-white'}`}>
                            {sel && <span className="text-white text-lg font-bold">✓</span>}
                          </div>
                          <span className={`font-medium text-[#1A1A1A] ${sel ? 'text-[#8B7355]' : ''}`}>{piece}</span>
                        </button>
                        {sel && (
                          <div className="p-4 pt-3 border-t border-[#8B7355]/30 bg-[#8B7355]/5">
                            <p className="text-sm font-medium text-[#8B7355] mb-3">Stigmates observés :</p>
                            <div className="flex flex-wrap gap-2">
                              {STIGMATES.map((stig) => (
                                <button key={stig} type="button" onClick={() => {
                                  const newP = [...form.pieces_sinistrees];
                                  if (sel.stigmates.includes(stig)) newP[idx].stigmates = newP[idx].stigmates.filter(s => s !== stig);
                                  else newP[idx].stigmates.push(stig);
                                  setForm({ ...form, pieces_sinistrees: newP });
                                }}
                                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all border-2 ${sel.stigmates.includes(stig) ? 'bg-[#8B7355] text-white shadow-md border-[#8B7355]' : 'bg-white border-[#8B7355]/60 text-[#1A1A1A] hover:border-[#8B7355]'}`}>
                                  {stig}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ÉTAPE 6: Constats + Photos désordres */}
            {step === 6 && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-center text-[#1A1A1A]">Constats à l'arrivée</h2>
                <div><Label>Description des constats</Label><Textarea value={form.constats_arrivee} onChange={(e) => setForm({ ...form, constats_arrivee: e.target.value })} className="mt-1" rows={4} placeholder="Décrivez ce que vous observez en arrivant..." /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Nom du client</Label><Input value={form.client_nom} onChange={(e) => setForm({ ...form, client_nom: e.target.value })} className="mt-1" /></div>
                  <div><Label className="text-xs">Téléphone</Label><Input value={form.client_telephone} onChange={(e) => setForm({ ...form, client_telephone: e.target.value })} className="mt-1" /></div>
                </div>

                {/* Références dossier (à noter pendant l'intervention) */}
                <div className="p-4 border-2 border-[#8B7355]/30 rounded-xl bg-[#F8F5F0] space-y-3">
                  <p className="text-sm font-semibold text-[#8B7355]">Références du dossier</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label className="text-xs">N° de sinistre</Label><Input value={form.numero_sinistre} onChange={(e) => setForm({ ...form, numero_sinistre: e.target.value })} className="mt-1" placeholder="Ex: SIN-2026-778" data-testid="numero-sinistre-input" /></div>
                    <div><Label className="text-xs">N° de dossier (donneur d'ordre)</Label><Input value={form.numero_dossier} onChange={(e) => setForm({ ...form, numero_dossier: e.target.value })} className="mt-1" placeholder="Ex: DOS-99812" data-testid="numero-dossier-input" /></div>
                    <div><Label className="text-xs">Nom de l'assurance</Label><Input value={form.nom_assurance} onChange={(e) => setForm({ ...form, nom_assurance: e.target.value })} className="mt-1" placeholder="Ex: AXA Assurances" data-testid="nom-assurance-input" /></div>
                    <div><Label className="text-xs">Nom de l'expert</Label><Input value={form.nom_expert} onChange={(e) => setForm({ ...form, nom_expert: e.target.value })} className="mt-1" placeholder="Ex: M. Bernard" data-testid="nom-expert-input" /></div>
                    <div className="sm:col-span-2"><Label className="text-xs">Cabinet d'expertise</Label><Input value={form.cabinet_expertise} onChange={(e) => setForm({ ...form, cabinet_expertise: e.target.value })} className="mt-1" placeholder="Ex: Cabinet Expertys" data-testid="cabinet-expertise-input" /></div>
                  </div>
                </div>

                <StablePhotoUpload 
                  title="Photos des désordres (6 minimum) — ajoutez une légende"
                  photos={form.photos_desordres}
                  onUpload={(files) => uploadPhoto(files, 'photos_desordres', true)}
                  onRemove={(i) => removePhoto('photos_desordres', i)}
                  onEdit={(i) => editFieldPhoto('photos_desordres', i)}
                  uploading={uploading}
                  showCaptions={true}
                  captions={form.photos_desordres_legendes}
                  captionTestIdPrefix="desordre-legende"
                  onCaptionChange={(i, val) => setForm(prev => {
                    const arr = [...(prev.photos_desordres_legendes || [])];
                    while (arr.length <= i) arr.push('');
                    arr[i] = val;
                    return { ...prev, photos_desordres_legendes: arr };
                  })}
                />
              </div>
            )}

            {/* ÉTAPE 7: Mesures humidité - AMÉLIORÉ */}
            {step === 7 && (
              <div className="space-y-6">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Droplets className="h-8 w-8 text-blue-600" />
                  </div>
                  <h2 className="text-xl font-bold text-[#1A1A1A]">Mesures d'humidité</h2>
                  <p className="text-[#6B7280] text-sm">Lieu, support, valeur et photo pour chaque mesure</p>
                </div>
                
                <div className="space-y-6">
                  {form.mesures_humidite.map((m, i) => (
                    <div key={i} className="p-4 border-2 border-blue-200 rounded-xl bg-blue-50/50">
                      <div className="flex justify-between items-center mb-3">
                        <span className="font-semibold text-blue-800">Mesure {i + 1}</span>
                        {form.mesures_humidite.length > 1 && (
                          <Button variant="ghost" size="sm" onClick={() => removeMesureHumidite(i)} className="text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <Label className="text-xs">Zone / Pièce</Label>
                          <DebouncedInput placeholder="Ex: Salon" value={m.zone} onChange={(e) => {
                            const n = [...form.mesures_humidite]; n[i].zone = e.target.value; setForm({ ...form, mesures_humidite: n });
                          }} className="mt-1" />
                        </div>
                        <div>
                          <Label className="text-xs">Support</Label>
                          <DebouncedInput placeholder="Ex: Mur placo" value={m.support} onChange={(e) => {
                            const n = [...form.mesures_humidite]; n[i].support = e.target.value; setForm({ ...form, mesures_humidite: n });
                          }} className="mt-1" />
                        </div>
                      </div>
                      <div className="mb-3">
                        <Label className="text-xs">Valeur mesurée</Label>
                        <DebouncedInput placeholder="Ex: 85%" value={m.valeur} onChange={(e) => {
                          const n = [...form.mesures_humidite]; n[i].valeur = e.target.value; setForm({ ...form, mesures_humidite: n });
                        }} className="mt-1" />
                      </div>
                      <StablePhotoUpload 
                        title="Photos de cette mesure"
                        photos={m.photos}
                        onUpload={(files) => uploadPhoto(files, null, true, i, 'mesures_humidite')}
                        onRemove={(idx) => removePhoto(null, idx, i, 'mesures_humidite')}
                        onEdit={(idx) => editArrayPhoto('mesures_humidite', i, idx)}
                        uploading={uploading}
                      />
                    </div>
                  ))}
                </div>
                
                <Button onClick={addMesureHumidite} variant="outline" className="w-full border-dashed border-2">
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter une mesure
                </Button>
              </div>
            )}

            {/* ÉTAPE 8: Mesures thermiques - AMÉLIORÉ */}
            {step === 8 && (
              <div className="space-y-6">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Thermometer className="h-8 w-8 text-red-600" />
                  </div>
                  <h2 className="text-xl font-bold text-[#1A1A1A]">Mesures thermiques</h2>
                  <p className="text-[#6B7280] text-sm">Zone, observation et photo caméra thermique</p>
                </div>
                
                <div className="space-y-6">
                  {form.mesures_thermiques.map((m, i) => (
                    <div key={i} className="p-4 border-2 border-red-200 rounded-xl bg-red-50/50">
                      <div className="flex justify-between items-center mb-3">
                        <span className="font-semibold text-red-800">Zone {i + 1}</span>
                        {form.mesures_thermiques.length > 1 && (
                          <Button variant="ghost" size="sm" onClick={() => removeMesureThermique(i)} className="text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <div className="mb-3">
                        <Label className="text-xs">Zone inspectée</Label>
                        <DebouncedInput placeholder="Ex: Angle mur/plafond chambre" value={m.zone} onChange={(e) => {
                          const n = [...form.mesures_thermiques]; n[i].zone = e.target.value; setForm({ ...form, mesures_thermiques: n });
                        }} className="mt-1" />
                      </div>
                      <div className="mb-3">
                        <Label className="text-xs">Observation</Label>
                        <DebouncedTextarea placeholder="Ex: Pont thermique visible, différence de température..." value={m.observation} onChange={(e) => {
                          const n = [...form.mesures_thermiques]; n[i].observation = e.target.value; setForm({ ...form, mesures_thermiques: n });
                        }} className="mt-1" rows={2} />
                      </div>
                      <StablePhotoUpload 
                        title="Photos caméra thermique"
                        photos={m.photos}
                        onUpload={(files) => uploadPhoto(files, null, true, i, 'mesures_thermiques')}
                        onRemove={(idx) => removePhoto(null, idx, i, 'mesures_thermiques')}
                        onEdit={(idx) => editArrayPhoto('mesures_thermiques', i, idx)}
                        uploading={uploading}
                      />
                    </div>
                  ))}
                </div>
                
                <Button onClick={addMesureThermique} variant="outline" className="w-full border-dashed border-2">
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter une zone
                </Button>
              </div>
            )}

            {/* ÉTAPE 9-10: Procédés - AMÉLIORÉ (pages regroupées) */}
            {step === 9 && (
              <div className="space-y-6">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-[#8B7355]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Wrench className="h-8 w-8 text-[#8B7355]" />
                  </div>
                  <h2 className="text-xl font-bold text-[#1A1A1A]">Procédés effectués</h2>
                  <p className="text-[#6B7280] text-sm">Sélectionnez un procédé pour pré-remplir automatiquement</p>
                </div>

                {/* Carrousel de sélection rapide des procédés */}
                <div className="bg-gradient-to-r from-[#1A1A1A] to-[#2D2D2D] rounded-xl p-4 mb-6">
                  <p className="text-white text-sm font-medium mb-3">➕ Ajouter un procédé :</p>
                  <div className="flex flex-wrap gap-2">
                    {PROCEDES_LISTE.map((proc) => (
                      <button 
                        key={proc} 
                        type="button" 
                        onClick={() => {
                          const template = PROCEDES_TYPES[proc];
                          const newProcede = {
                            type: proc,
                            nom: proc,
                            description: template?.description || '',
                            resultat: template?.resultat || '',
                            photos: []
                          };
                          setForm(prev => ({ ...prev, procedes: [...prev.procedes.filter(p => p.type), newProcede] }));
                          toast.success(`Procédé "${proc}" ajouté avec description pré-remplie`);
                        }}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-[#8B7355] text-white hover:bg-[#A08060] transition-all shadow-md hover:shadow-lg flex items-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        {proc}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Liste des procédés ajoutés */}
                <div className="space-y-6">
                  {form.procedes.filter(p => p.type).length === 0 ? (
                    <div className="text-center py-8 border-2 border-dashed border-[#8B7355]/30 rounded-xl">
                      <Wrench className="h-12 w-12 text-[#8B7355]/30 mx-auto mb-3" />
                      <p className="text-[#6B7280]">Aucun procédé ajouté</p>
                      <p className="text-sm text-[#9CA3AF]">Cliquez sur un procédé ci-dessus pour l'ajouter</p>
                    </div>
                  ) : (
                    form.procedes.map((p, i) => (p.type ? (
                      <div key={i} className="border-2 border-[#8B7355]/30 rounded-xl overflow-hidden bg-white shadow-lg">
                        {/* En-tête du procédé */}
                        <div className="bg-gradient-to-r from-[#8B7355] to-[#A08060] p-4 flex justify-between items-center">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                              <Wrench className="h-5 w-5 text-white" />
                            </div>
                          <div className="flex-1 min-w-0">
                            <DebouncedInput
                              value={p.nom ?? p.type}
                              onChange={(e) => {
                                const idx = i;
                                const n = [...form.procedes];
                                n[idx].nom = e.target.value;
                                setForm({ ...form, procedes: n });
                              }}
                              placeholder="Nom du procédé (ex: Colorant lavabo)"
                              className="font-bold text-white text-lg bg-white/15 border-white/30 placeholder:text-white/50 focus:bg-white/25 h-9"
                              data-testid={`procede-nom-input-${i}`}
                            />
                            <p className="text-white/70 text-xs mt-1">Procédé {i + 1} · modifiez le nom si besoin</p>
                          </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => {
                              const n = form.procedes.filter((_, idx) => idx !== i);
                              setForm({ ...form, procedes: n.length ? n : [{ type: '', description: '', resultat: '', photos: [] }] });
                            }} 
                            className="text-white hover:bg-white/20"
                          >
                            <Trash2 className="h-5 w-5" />
                          </Button>
                        </div>
                        
                        <div className="p-4 space-y-4">
                          {/* Description */}
                          <div>
                            <Label className="text-sm font-semibold text-[#1A1A1A] flex items-center gap-2">
                              <FileText className="h-4 w-4 text-[#8B7355]" />
                              Description du procédé
                            </Label>
                            <p className="text-xs text-[#6B7280] mb-2">Modifiez si nécessaire</p>
                            <DebouncedTextarea 
                              placeholder="Décrivez le procédé utilisé..." 
                              value={p.description} 
                              onChange={(e) => {
                                const idx = i;
                                const n = [...form.procedes]; 
                                n[idx].description = e.target.value; 
                                setForm({ ...form, procedes: n });
                              }} 
                              className="mt-1 border-[#8B7355]/30 focus:border-[#8B7355]" 
                              rows={4} 
                            />
                          </div>
                          
                          {/* Résultat */}
                          <div>
                            <Label className="text-sm font-semibold text-[#1A1A1A] flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              Résultat / Observation
                            </Label>
                            <p className="text-xs text-[#6B7280] mb-2">Complétez vos observations</p>
                            <DebouncedTextarea 
                              placeholder="Qu'avez-vous constaté ?" 
                              value={p.resultat} 
                              onChange={(e) => {
                                const idx = i;
                                const n = [...form.procedes]; 
                                n[idx].resultat = e.target.value; 
                                setForm({ ...form, procedes: n });
                              }} 
                              className="mt-1 border-[#8B7355]/30 focus:border-[#8B7355]" 
                              rows={2} 
                            />
                          </div>
                          
                          {/* Photos */}
                          <div className="pt-2 border-t border-gray-100">
                            <StablePhotoUpload 
                              title={`📸 Photos - ${p.nom || p.type}`}
                              photos={p.photos}
                              onUpload={(files) => {
                                const idx = i;
                                uploadPhoto(files, null, true, idx, 'procedes');
                              }}
                              onRemove={(photoIdx) => {
                                const idx = i;
                                removePhoto(null, photoIdx, idx, 'procedes');
                              }}
                              onEdit={(photoIdx) => editArrayPhoto('procedes', i, photoIdx)}
                              uploading={uploading}
                              showCaptions={true}
                              captions={p.photos_legendes}
                              captionTestIdPrefix={`procede-${i}-legende`}
                              onCaptionChange={(photoIdx, val) => setForm(prev => {
                                const procs = [...prev.procedes];
                                const leg = [...(procs[i].photos_legendes || [])];
                                while (leg.length <= photoIdx) leg.push('');
                                leg[photoIdx] = val;
                                procs[i] = { ...procs[i], photos_legendes: leg };
                                return { ...prev, procedes: procs };
                              })}
                            />
                          </div>
                        </div>
                      </div>
                    ) : null))
                  )}
                </div>
              </div>
            )}

            {/* ÉTAPE 10: Récapitulatif procédés (optionnel, peut être sauté) */}
            {step === 10 && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-center text-[#1A1A1A]">Récapitulatif des procédés</h2>
                {form.procedes.filter(p => p.type).length === 0 ? (
                  <div className="text-center py-8 text-[#6B7280]">
                    <Wrench className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>Aucun procédé enregistré</p>
                    <Button onClick={() => setStep(9)} variant="outline" className="mt-4">
                      Retour à l'étape procédés
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {form.procedes.filter(p => p.type).map((p, i) => (
                      <div key={i} className="p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="bg-[#8B7355] text-white px-3 py-1 rounded-full text-sm">{p.nom || p.type}</span>
                        </div>
                        {p.description && <p className="text-sm text-gray-700 mb-1">{p.description}</p>}
                        {p.resultat && <p className="text-sm text-gray-500 italic">{p.resultat}</p>}
                        {p.photos.length > 0 && (
                          <div className="flex gap-2 mt-2">
                            {p.photos.slice(0, 3).map((photo, j) => (
                              <img key={j} src={photo} alt="" className="w-16 h-16 object-cover rounded" />
                            ))}
                            {p.photos.length > 3 && <span className="text-xs text-gray-500">+{p.photos.length - 3}</span>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Comparaison Avant / Après */}
                <div className="pt-6 border-t border-gray-200" data-testid="avant-apres-section">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-bold text-[#1A1A1A]">Photos Avant / Après</h3>
                      <p className="text-xs text-[#6B7280]">Comparaison côte à côte pour le rapport</p>
                    </div>
                    <Button type="button" onClick={addAvantApresPair} variant="outline" size="sm" className="border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355] hover:text-white" data-testid="add-avant-apres-btn">
                      <Plus className="h-4 w-4 mr-1" />Ajouter
                    </Button>
                  </div>

                  {(form.photos_avant_apres || []).length === 0 ? (
                    <div className="text-center py-6 text-[#6B7280] bg-gray-50 rounded-xl">
                      <Camera className="h-8 w-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">Aucune comparaison. Cliquez sur « Ajouter » pour créer une paire avant/après.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {form.photos_avant_apres.map((pair, i) => (
                        <div key={i} className="p-4 bg-gray-50 rounded-xl" data-testid={`avant-apres-pair-${i}`}>
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-semibold text-[#8B7355]">Comparaison {i + 1}</span>
                            <button type="button" onClick={() => removeAvantApresPair(i)} className="text-red-500" data-testid={`remove-avant-apres-${i}`}>
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            {['avant', 'apres'].map((which) => (
                              <div key={which}>
                                <p className={`text-xs font-bold mb-1 ${which === 'avant' ? 'text-red-600' : 'text-green-600'}`}>
                                  {which === 'avant' ? 'AVANT' : 'APRÈS'}
                                </p>
                                {pair[which] ? (
                                  <div className="relative">
                                    <img src={pair[which]} alt={which} className="w-full h-32 object-cover rounded-lg" />
                                    <button type="button" onClick={() => removeAvantApresPhoto(i, which)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1.5 shadow" data-testid={`remove-photo-${which}-${i}`}>
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    <PhotoCapture uploading={uploading} label="Photo" subLabel="Appareil" capture={true}
                                      onCapture={(files) => uploadAvantApres(files, i, which)} />
                                    <PhotoCapture uploading={uploading} label="Galerie" subLabel="Importer" capture={false}
                                      onCapture={(files) => uploadAvantApres(files, i, which)} />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                          <Input value={pair.legende || ''} onChange={(e) => setForm(prev => { const arr = [...prev.photos_avant_apres]; arr[i] = { ...arr[i], legende: e.target.value }; return { ...prev, photos_avant_apres: arr }; })}
                            placeholder="Légende (ex: Plafond salon)" className="mt-3" data-testid={`avant-apres-legende-${i}`} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ÉTAPE 11: Conclusion */}
            {step === 11 && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-center text-[#1A1A1A]">Conclusion</h2>
                <div><Label>Résumé des constats</Label><DebouncedTextarea value={form.resume_constats} onChange={(e) => setForm({ ...form, resume_constats: e.target.value })} className="mt-1" rows={4} placeholder="Synthèse de vos observations..." /></div>
                <div><Label>Origine probable</Label><DebouncedTextarea value={form.origine_probable} onChange={(e) => setForm({ ...form, origine_probable: e.target.value })} className="mt-1" rows={2} placeholder="Cause probable identifiée..." /></div>
                <div><Label>Préconisations</Label><DebouncedTextarea value={form.preconisations} onChange={(e) => setForm({ ...form, preconisations: e.target.value })} className="mt-1" rows={2} placeholder="Travaux recommandés..." /></div>
                <div><Label>Points urgents</Label><DebouncedTextarea value={form.points_urgents} onChange={(e) => setForm({ ...form, points_urgents: e.target.value })} className="mt-1" rows={2} placeholder="Actions immédiates nécessaires..." /></div>
                
                <div className="p-4 border-2 border-amber-300 rounded-xl bg-amber-50">
                  <div className="flex items-center gap-3">
                    <Checkbox checked={form.franchise} onCheckedChange={(c) => setForm({ ...form, franchise: c })} />
                    <Label className="font-semibold text-amber-800">💰 Franchise à récupérer</Label>
                  </div>
                  {form.franchise && <Input placeholder="Montant (ex: 150€)" value={form.franchise_montant} onChange={(e) => setForm({ ...form, franchise_montant: e.target.value })} className="mt-3" />}
                </div>

                {/* Documents PDF à faire signer au client (accessibles à la création) */}
                <div className="p-4 bg-white rounded-xl border-2 border-[#8B7355]/30" data-testid="form-documents-section">
                  <Label className="text-sm font-semibold text-[#8B7355] flex items-center gap-2 mb-1">
                    <FileText className="h-4 w-4" />
                    Documents PDF à faire signer
                  </Label>
                  <p className="text-xs text-[#6B7280] mb-3">Ajoutez ou retrouvez les PDF (y compris ceux transmis par la secrétaire). Ouvrez-les, faites-les signer au client et écrivez dessus (date, franchise…).</p>
                  {(form.pieces_jointes || []).length > 0 && (
                    <ul className="space-y-2 mb-3" data-testid="form-attachments-list">
                      {form.pieces_jointes.map((a) => (
                        <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 p-2 rounded-lg border border-[#E5E0D8] bg-[#F8F5F0]" data-testid="form-attachment-item">
                          <span className="flex items-center gap-2 min-w-0 text-sm flex-1">
                            <FileText className="h-4 w-4 text-red-600 flex-shrink-0" />
                            <span className="truncate">{a.name}{a.signed && <span className="ml-1 text-xs text-emerald-700 font-semibold">✓ signé</span>}</span>
                          </span>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <a href={fileHref(a.url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 border border-[#8B7355] text-[#8B7355] px-2 py-1 rounded text-xs hover:bg-[#8B7355]/10" data-testid="form-attachment-open-btn"><Eye className="h-3.5 w-3.5" />Ouvrir</a>
                            <button type="button" onClick={() => { setSignAtt(a); setSigPreview(null); setTimeout(sigClear, 100); }} className="inline-flex items-center gap-1 border border-emerald-500 text-emerald-700 px-2 py-1 rounded text-xs hover:bg-emerald-50" data-testid="form-attachment-sign-btn"><Edit3 className="h-3.5 w-3.5" />Signer / Écrire</button>
                            <button type="button" onClick={() => setForm(f => ({ ...f, pieces_jointes: f.pieces_jointes.filter(p => p.id !== a.id) }))} className="text-red-500 hover:text-red-700 p-1"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-[#8B7355]/30 rounded-lg cursor-pointer hover:bg-[#8B7355]/5">
                    <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid="form-attachment-input" disabled={uploadingPJ}
                      onChange={async (e) => {
                        const file = e.target.files?.[0]; e.target.value = '';
                        if (!file) return;
                        setUploadingPJ(true);
                        try {
                          const fd = new FormData(); fd.append('file', file);
                          const res = await axios.post(API + '/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
                          const att = { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: file.name, url: res.data.url, size: file.size, uploaded_by_name: 'Création', uploaded_at: new Date().toISOString() };
                          setForm(f => ({ ...f, pieces_jointes: [...(f.pieces_jointes || []), att] }));
                        } catch (err) { toast.error('Échec du téléversement'); }
                        setUploadingPJ(false);
                      }} />
                    <Upload className="h-5 w-5 text-[#8B7355]" />
                    <span className="text-sm text-[#6B7280]">{uploadingPJ ? 'Envoi...' : 'Ajouter un PDF'}</span>
                  </label>
                </div>
              </div>
            )}

            {/* ÉTAPE 12: Signature et Satisfaction */}
            {step === 12 && (
              <div className="space-y-6">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <PenTool className="h-8 w-8 text-green-600" />
                  </div>
                  <h2 className="text-xl font-bold text-[#1A1A1A]">Validation & Satisfaction</h2>
                  <p className="text-[#6B7280] text-sm">Finalisez l'intervention avec le client</p>
                </div>
                
                {/* Texte de certification */}
                <div className="p-4 bg-[#8B7355]/10 rounded-xl border-2 border-[#8B7355]/30">
                  <Label className="text-sm font-semibold text-[#8B7355] flex items-center gap-2 mb-2">
                    <FileText className="h-4 w-4" />
                    Attestation d'intervention
                  </Label>
                  <Textarea 
                    value={form.texte_validation || "Nous certifions être intervenus chez le client mentionné ci-dessus. Les travaux de recherche de fuite ont été réalisés conformément aux règles de l'art et à l'entière satisfaction du client."}
                    onChange={(e) => setForm({ ...form, texte_validation: e.target.value })}
                    className="border-[#8B7355]/30 focus:border-[#8B7355] bg-white"
                    rows={3}
                  />
                </div>
                
                {/* Note de satisfaction client */}
                <div className="p-4 bg-white rounded-xl border-2 border-[#8B7355]/30">
                  <Label className="text-sm font-semibold text-[#8B7355] flex items-center gap-2 mb-3">
                    <Star className="h-4 w-4" />
                    Note de satisfaction client
                  </Label>
                  <p className="text-xs text-[#6B7280] mb-4">Demandez au client d'évaluer notre prestation</p>
                  
                  <div className="flex items-center justify-center flex-wrap gap-2">
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setForm({ ...form, satisfaction_note: n })}
                        className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                          form.satisfaction_note === n
                            ? 'bg-[#8B7355] text-white shadow-lg scale-110 ring-2 ring-[#8B7355] ring-offset-2'
                            : 'bg-gray-100 text-gray-600 hover:bg-[#8B7355]/20 hover:text-[#8B7355]'
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  
                  {form.satisfaction_note !== undefined && form.satisfaction_note !== null && (
                    <div className={`mt-4 p-3 rounded-lg text-center ${
                      form.satisfaction_note >= 8 ? 'bg-green-100 text-green-700' : 
                      form.satisfaction_note >= 6 ? 'bg-blue-100 text-blue-700' : 
                      form.satisfaction_note >= 4 ? 'bg-amber-100 text-amber-700' : 
                      'bg-red-100 text-red-700'
                    }`}>
                      <p className="font-semibold text-lg">
                        {form.satisfaction_note >= 8 ? '⭐ Excellent travail !' : 
                         form.satisfaction_note >= 6 ? '👍 Client satisfait' : 
                         form.satisfaction_note >= 4 ? '😐 Satisfaction moyenne' : 
                         '😕 À améliorer'}
                      </p>
                      <p className="text-sm opacity-80">Note : {form.satisfaction_note}/10</p>
                    </div>
                  )}
                </div>
                
                {/* Signature */}
                <div className="p-4 bg-white rounded-xl border-2 border-[#8B7355]/30">
                  <Label className="text-sm font-semibold text-[#8B7355] flex items-center gap-2 mb-3">
                    <PenTool className="h-4 w-4" />
                    Signature du client
                  </Label>
                  
                  {form.client_signature ? (
                    <div className="text-center">
                      <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4 mb-4">
                        <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                        <p className="text-green-700 font-semibold">Signature enregistrée !</p>
                      </div>
                      <img src={form.client_signature} alt="Signature" className="max-h-32 mx-auto border-2 border-gray-200 rounded-xl mb-4" />
                      <Button variant="outline" onClick={() => setForm({ ...form, client_signature: null })} className="border-[#8B7355] text-[#8B7355]">
                        Refaire la signature
                      </Button>
                    </div>
                  ) : (
                    <SignaturePad 
                      onSave={(sig) => {
                        setForm({ ...form, client_signature: sig, heure_fin: new Date().toISOString() });
                      }} 
                      title="Le client signe pour valider l'intervention" 
                    />
                  )}
                </div>
                
                {/* Récapitulatif horaires */}
                {form.heure_debut && (
                  <div className="p-4 bg-gray-100 rounded-xl">
                    <div className="flex justify-between items-center">
                      <div className="text-center flex-1">
                        <p className="text-xs text-gray-500">Début</p>
                        <p className="font-semibold text-[#8B7355]">
                          {new Date(form.heure_debut).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <div className="text-2xl text-gray-300">→</div>
                      <div className="text-center flex-1">
                        <p className="text-xs text-gray-500">Fin</p>
                        <p className="font-semibold text-[#8B7355]">
                          {form.heure_fin 
                            ? new Date(form.heure_fin).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                            : 'En cours...'
                          }
                        </p>
                      </div>
                      <div className="text-center flex-1 border-l border-gray-300 pl-4">
                        <p className="text-xs text-gray-500">Durée</p>
                        <p className="font-semibold text-[#8B7355]">
                          {form.heure_fin 
                            ? `${Math.round((new Date(form.heure_fin) - new Date(form.heure_debut)) / (1000 * 60))} min`
                            : '--'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <p className="text-sm text-blue-700">
                    💡 Après validation, vous pourrez personnaliser le compte-rendu et ajouter des documents depuis l'éditeur de rapport.
                  </p>
                </div>

                {/* Remerciement + étoiles + QR avis Google (uniquement dans l'app, après signature) */}
                {form.client_signature && (
                  <div className="p-5 bg-white rounded-xl border-2 border-[#8B7355]/30 text-center" data-testid="intervention-thanks-qr">
                    {form.satisfaction_note !== undefined && form.satisfaction_note !== null && (
                      <p className="text-sm text-[#6B7280] mb-2">
                        Note donnée par l'assuré(e) : <span className="font-bold text-[#8B7355] text-lg">{form.satisfaction_note}/10</span>
                      </p>
                    )}
                    <h3 className="text-lg font-bold text-[#8B7355]">Merci de votre confiance !</h3>
                    <p className="text-sm text-[#6B7280] mb-4 max-w-sm mx-auto">
                      Votre avis compte énormément pour nous. Scannez ce QR code pour nous laisser une note et un commentaire sur Google. Merci beaucoup !
                    </p>
                    <img src="/kardif_qr_review.png" alt="Avis Google KARDIF" className="w-44 h-44 object-contain mx-auto" data-testid="intervention-qr-image" />
                  </div>
                )}
              </div>
            )}

          </CardContent>
        </Card>
      </main>

      {/* Footer navigation - Mobile optimized */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t p-4 z-30 safe-area-bottom">
        <div className="max-w-2xl mx-auto flex justify-between items-center">
          <Button variant="outline" onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1} className="px-4">
            <ArrowLeft className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Précédent</span>
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[#8B7355]">{step}/{totalSteps}</span>
          </div>
          {step === totalSteps ? (
            <Button onClick={submit} disabled={loading || !form.client_signature} className="bg-green-600 hover:bg-green-700 text-white px-4">
              {loading ? 'Envoi...' : <><Save className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Soumettre</span></>}
            </Button>
          ) : (
            <Button onClick={() => setStep(s => Math.min(totalSteps, s + 1))} className="bg-[#8B7355] hover:bg-[#7A6548] text-white px-4">
              <span className="hidden sm:inline">Suivant</span><ArrowRight className="h-4 w-4 sm:ml-2" />
            </Button>
          )}
        </div>
      </footer>

      {/* Dialogue Signer / Écrire sur un PDF pendant la création */}
      <Dialog open={!!signAtt} onOpenChange={(o) => { if (!o) { setSignAtt(null); setSigPreview(null); } }}>
        <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader><DialogTitle className="text-[#8B7355]">Signer / Écrire — {signAtt?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Nom du signataire (optionnel)" className="w-full border rounded px-3 py-2 text-sm" data-testid="form-signer-name-input" />
            <div>
              <label className="text-xs font-semibold text-[#8B7355] block mb-1">1. Dessinez la signature (optionnel)</label>
              <div className="border-2 border-dashed border-[#8B7355]/40 rounded-lg bg-white">
                <canvas ref={sigCanvasRef} width={520} height={180} className="w-full touch-none cursor-crosshair"
                  onPointerDown={sigStart} onPointerMove={sigMove} onPointerUp={sigEnd} onPointerLeave={sigEnd} data-testid="form-signature-canvas" />
              </div>
              <div className="flex justify-end mt-1">
                <Button variant="outline" size="sm" onClick={sigClear} data-testid="form-signature-clear-btn">Effacer</Button>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#8B7355] block mb-1">2. Placez la signature et/ou ajoutez du texte (date, franchise…)</label>
              {signAtt && (
                <PdfSignPlacer pdfUrl={fileHref(signAtt.url)} signatureUrl={sigPreview} onChange={setSignPlacement} />
              )}
            </div>
            <div className="flex justify-end pt-1">
              <Button onClick={submitSignPJ} disabled={signingPJ} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="form-signature-save-btn">{signingPJ ? 'Enregistrement...' : 'Valider'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <PhotoEditor
        open={photoEditor.open}
        imageSrc={photoEditor.src}
        uploading={editorUploading}
        onClose={() => setPhotoEditor({ open: false, src: null, onSaveUrl: null })}
        onSave={handlePhotoEditorSave}
      />
    </div>
  );
}

export default InterventionForm;
